/**
 * Numbers Audit — /admin/qa/numbers
 *
 * Renders every golden fixture through the canonical deal-metrics engine
 * and highlights any cell whose actual value disagrees with the frozen
 * expectation. A green table means "the whole app can trust these
 * numbers"; any red cell is a live regression against the assertions in
 * tests/test_deal_metrics_golden.py.
 *
 * This is the "one-glance visual proof of coherence" the operator asked
 * for after we shipped 3 different MAO bugs in a single session.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, RefreshCw, Loader2, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";


function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}


function Cell({ cell }) {
  const ok = cell.ok;
  return (
    <td
      data-testid={`qa-cell-${cell.key}`}
      className={`px-3 py-2 text-sm tabular-nums ${
        ok
          ? "text-slate-800"
          : "bg-rose-50 text-rose-900 ring-1 ring-inset ring-rose-300"
      }`}
      title={ok ? "" : `Expected ${fmt(cell.expected)} · Got ${fmt(cell.got)}`}
    >
      <div className="flex items-center gap-1.5">
        {ok
          ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
          : <XCircle className="w-3 h-3 text-rose-500 shrink-0" />}
        <span>{fmt(cell.got)}</span>
        {!ok && (
          <span className="text-[10px] text-rose-600 font-mono ml-1">
            (want {fmt(cell.expected)})
          </span>
        )}
      </div>
    </td>
  );
}


export default function NumbersAuditPage() {
  const [audit, setAudit]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/qa/numbers");
      setAudit(data);
    } catch (err) {
      // Show the raw error inline — this is a QA tool, not a customer surface.
      setAudit({ error: err?.response?.data?.detail || String(err) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !audit) {
    return (
      <div className="p-12 grid place-items-center">
        <Loader2 className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (audit?.error) {
    return (
      <div className="p-6 text-sm text-rose-700 bg-rose-50 rounded-lg">
        Audit endpoint failed: {audit.error}
      </div>
    );
  }

  const { totals, rows, engine_version, rule_percent, assign_fee_default } = audit;
  const banner = totals.passing
    ? { color: "emerald", label: "All numbers agree", icon: ShieldCheck }
    : { color: "rose",    label: `${totals.failed_cells} cell(s) disagree`, icon: XCircle };
  const Icon = banner.icon;

  // Union of every `cell.key` across rows, in stable display order.
  const COL_ORDER = ["arv", "arv_source", "repairs", "flip_70", "wholesale", "margin", "roi"];
  const columns = COL_ORDER.filter(
    (k) => rows.some((r) => r.cells.some((c) => c.key === k)),
  );

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="qa-numbers-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-600 font-bold">
            Internal QA · Numbers Audit
          </div>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-900">
            Numbers Audit
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Every golden fixture in the pytest suite, run live through
            <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-100 text-[11px]">
              services/deal_metrics.compute_metrics
            </code>
            — same engine every Deal Card header, Strategy pill, and PDF
            cover consumes. A red cell means a UI regression against the
            frozen expectation.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="qa-refresh"
          className="text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" />
          Re-run audit
        </button>
      </div>

      {/* Big status banner */}
      <div
        data-testid="qa-banner"
        className={`rounded-xl border p-5 flex items-center gap-4 ${
          totals.passing
            ? "border-emerald-200 bg-emerald-50/60"
            : "border-rose-300 bg-rose-50"
        }`}
      >
        <Icon className={`w-8 h-8 ${
          totals.passing ? "text-emerald-500" : "text-rose-500"
        }`} />
        <div className="flex-1">
          <div className={`text-lg font-bold ${
            totals.passing ? "text-emerald-900" : "text-rose-900"
          }`}>
            {banner.label}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {totals.fixtures} fixtures · {totals.total_cells} cells checked ·
            engine <code className="font-mono">{engine_version}</code> · rule
            {" "}<code className="font-mono">{Math.round(rule_percent * 100)}%</code>
            {" "}· default fee <code className="font-mono">
              ${assign_fee_default.toLocaleString()}
            </code>
          </div>
        </div>
        <Link
          to="/v3"
          className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
        >
          Back to app
        </Link>
      </div>

      {/* Audit grid */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <tr>
              <th className="px-3 py-2 text-left">Fixture</th>
              {columns.map((c) => (
                <th key={c} className="px-3 py-2 text-left">{c}</th>
              ))}
              <th className="px-3 py-2 text-left">Result</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                data-testid={`qa-row-${row.id}`}
                className="border-t border-slate-100 hover:bg-slate-50/50"
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{row.label}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {row.id}
                  </div>
                </td>
                {columns.map((colKey) => {
                  const cell = row.cells.find((c) => c.key === colKey);
                  if (!cell) return <td key={colKey} className="px-3 py-2 text-slate-300">—</td>;
                  return <Cell key={colKey} cell={cell} />;
                })}
                <td className="px-3 py-2">
                  {row.ok ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold">
                      <CheckCircle2 className="w-3 h-3" /> PASS
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-bold">
                      <XCircle className="w-3 h-3" /> FAIL
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footnote */}
      <div className="text-[10px] text-slate-400 font-mono">
        Fixtures mirror <code>tests/test_deal_metrics_golden.py</code>. Add a
        fixture there AND in <code>routers/qa_numbers.py::GOLDEN_FIXTURES</code>
        when you ship a new scenario worth freezing.
      </div>
    </div>
  );
}
